import React, { useState, useRef, useEffect, memo, useCallback } from 'react';
import bgPattern from "../assets/Onboarding-Form.jpg";
// To preload the background image, add the following to your public/index.html <head>:
// <link rel="preload" as="image" href="/assets/Onboarding-Form.jpg" />
import { useNavigate } from 'react-router-dom';
import { useGenerateOnboardingForm, useTaskStatus } from '../api/researchApi';
import { toast } from 'react-hot-toast';
interface FileUpload {
  id: string;
  name: string;
  size: number;
  status: 'uploading' | 'uploaded' | 'error';
  progress?: number;
}

type MultiSelectProps = {
  label: string;
  name: string;
  options: string[];
  selectedValues: string[];
  showOtherField?: boolean;
  otherFieldName?: string;
  otherFieldValue?: string;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
};

const ListingOnboardingForm: React.FC = () => {
  const [formData, setFormData] = useState({
    projectName: '',
    brandName: '',
    productInfo: '',
    amazonListingLink: '',
    websiteLink: '',
    instagramLink: '',
    currentProductInfo: '',
    listingElements: [] as string[],
    productCategory: [] as string[],
    categoryOther: '',
    notWorking: [] as string[],
    notWorkingOther: '',
    hasAssets: false,
    whiteBackgroundLink: '',
    oldImagesLink: '',
    lifestyleImageLink: '',
    userGeneratedContentLink: '',
    videoContentLink: '',
    noAssetsReason: ''
  });

  const [uploadedFiles, setUploadedFiles] = useState<FileUpload[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dropdownOpen, setDropdownOpen] = useState<{ [key: string]: boolean }>({});
  const navigate = useNavigate();
  
  // State for tracking form submission and content generation
  const [submittedProjectId, setSubmittedProjectId] = useState<string | null>(null);
  const [isGeneratingContent, setIsGeneratingContent] = useState(false);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [generatedForm, setGeneratedForm] = useState<any | null>(null);
  const pollRef = useRef<number | null>(null);
  
  // Background image readiness (avoid jank while typing/scrolling)
  const [bgReady, setBgReady] = useState(false);
  const [lowPerfMode, setLowPerfMode] = useState(false);
  const scrollTimeoutRef = useRef<number | null>(null);
  const interactionTimeoutRef = useRef<number | null>(null);
  useEffect(() => {
    const img = new Image();
    img.src = bgPattern;
    img.onload = () => setBgReady(true);
  }, []);

  // Reduce jank by disabling heavy background during scroll/typing
  useEffect(() => {
    const onScroll = () => {
      if (!lowPerfMode) setLowPerfMode(true);
      if (scrollTimeoutRef.current) window.clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = window.setTimeout(() => setLowPerfMode(false), 250);
    };
    window.addEventListener('scroll', onScroll as any, { passive: true } as any);
    return () => {
      window.removeEventListener('scroll', onScroll as any);
      if (scrollTimeoutRef.current) window.clearTimeout(scrollTimeoutRef.current);
    };
  }, [lowPerfMode]);

  const handleUserInput = useCallback(() => {
    if (!lowPerfMode) setLowPerfMode(true);
    if (interactionTimeoutRef.current) window.clearTimeout(interactionTimeoutRef.current);
    interactionTimeoutRef.current = window.setTimeout(() => setLowPerfMode(false), 250);
  }, [lowPerfMode]);

  // API hooks
  const generateOnboardingForm = useGenerateOnboardingForm();
  const { data: taskStatus } = useTaskStatus(taskId, { refetchInterval: 2000 });

  // Watch onboarding task status and capture generated form when ready
  useEffect(() => {
    if (!taskStatus) return;
    if ((taskStatus as any)?.status === 'completed') {
      const result = (taskStatus as any)?.result ?? taskStatus;
      setGeneratedForm(result);
      setIsGeneratingContent(false);
      toast.success('Onboarding form generated');
      // When onboarding task completes, redirect to creative brief for this project
      const pid = submittedProjectId || (result?.project_id ? String(result.project_id) : null);
      if (pid) {
        navigate(`/creative-brief/${pid}`);
      }
    }
    if ((taskStatus as any)?.status === 'failed') {
      setIsGeneratingContent(false);
      toast.error('Failed to generate onboarding form');
    }
  }, [taskStatus]);

  // Fallback: if still generating after 20s, start dashboard polling to catch brief_json
  useEffect(() => {
    if (!isGeneratingContent || !submittedProjectId) return;
    if (pollRef.current) return;
    const start = Date.now();
    const tick = async () => {
      const elapsed = Date.now() - start;
      if (elapsed < 20000) { // wait 20s before fallback
        pollRef.current = window.setTimeout(tick, 1000);
        return;
      }
      try {
        const url = `${import.meta.env.VITE_API_URL || 'http://localhost:8000'}/api/research-dashboard/${submittedProjectId}`;
        const res = await fetch(url);
        if (res.ok) {
          const data = await res.json();
          const brief = (data as any)?.creative_brief?.brief_json;
          if (brief) {
            navigate(`/creative-brief/${submittedProjectId}`);
            return;
          }
        }
      } catch (e) {
      }
      pollRef.current = window.setTimeout(tick, 3000);
    };
    pollRef.current = window.setTimeout(tick, 1000);
    return () => {
      if (pollRef.current) window.clearTimeout(pollRef.current);
      pollRef.current = null;
    };
  }, [isGeneratingContent, submittedProjectId, navigate]);

  const categoryOptions = [
    'Supplement',
    'Kitchen Tools',
    'Beauty & Skin Care',
    'Electronics',
    'Pet Products',
    'Fitness Gear',
    'Baby Products',
    'Home Decor',
    'Personal Care',
    'Outdoor Gear',
    'Others'
  ];

  const listingElementsOptions = [
    'Listing image',
    'Basic A+ Content',
    'Premium A+ Content',
    'Brand Story'
  ];

  const notWorkingOptions = [
    'Outdated or off-brand visuals',
    'low conversion(cvr is weak)',
    'key benefits aren\'t clear',
    'looks generic/ no differentiation',
    'doesn\'t reflect premium/value feel',
    'not optimized for mobile',
    'too much text, poor storytelling',
    'lacks lifestyle or human context',
    'doesn\'t answer customer question',
    'bad reviews from confusing info',
    'inconsistency across images, A+, storefront',
    'competitors visually outshines us',
    'updated product or packaging',
    'expanding to new market',
    'full brand refresh',
    'others'
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    handleUserInput();
    const { name, value, type } = e.target;
    
    // Use functional update for better performance
    setFormData(prev => {
      if (type === 'checkbox') {
        const checked = (e.target as HTMLInputElement).checked;
        if (name === 'hasAssets') {
          return { ...prev, [name]: checked };
        } else {
          // Handle multi-select checkboxes
          const currentArray = prev[name as keyof typeof prev] as string[];
          if (checked) {
            return { ...prev, [name]: [...currentArray, value] };
          } else {
            return { ...prev, [name]: currentArray.filter(item => item !== value) };
          }
        }
      } else {
        return { ...prev, [name]: value };
      }
    });
  };

  const MultiSelectDropdown = memo(({ 
    label, 
    name, 
    options, 
    selectedValues, 
    showOtherField = false,
    otherFieldName = '',
    otherFieldValue = '',
    isOpen,
    setIsOpen
  }: MultiSelectProps) => {
    return (
      <div className="relative">
        <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
        
        <div className="relative">
          {/* Dropdown Button */}
          <div
            className="w-full min-h-[42px] px-3 py-2 cursor-pointer flex items-center justify-between"
            style={{
              borderRadius: 12,
              border: '1px solid #E2D5FF',
              background: 'rgba(244, 243, 255, 0.60)'
            }}
            onClick={() => setIsOpen(!isOpen)}
          >
            <div className="flex flex-wrap gap-1 flex-1 mr-2">
              {selectedValues.length > 0 ? (
                selectedValues.map((value) => (
                  <span
                    key={value}
                    className="inline-flex items-center px-2 py-1 rounded-[6px] text-xs bg-[#E6E3FF] text-[#564DB8] border border-[#564DB8]"
                  >
                    {value}
                    <span
                      className="ml-1 text-black hover:text-[#362E7F] text-sm cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        const newArray = selectedValues.filter(item => item !== value);
                        setFormData((prev: any) => ({
                          ...prev,
                          [name]: newArray
                        }));
                      }}
                    >
                      ×
                    </span>
                  </span>
                ))
              ) : (
                <span className="text-gray-500">Select options</span>
              )}
            </div>
            <span className="flex-shrink-0 ml-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="10" viewBox="0 0 14 10" fill="none">
                <path fillRule="evenodd" clipRule="evenodd" d="M8.38144 9.26329L13.584 3.23799C13.7929 2.95995 13.9304 2.61486 13.9797 2.24459C14.029 1.87431 13.988 1.49476 13.8618 1.15197C13.7355 0.809179 13.5294 0.517879 13.2684 0.313401C13.0074 0.108923 12.7028 5.46748e-05 12.3914 0H1.60406C1.29367 0.00110072 0.990172 0.110327 0.730159 0.314507C0.470146 0.518688 0.264724 0.809104 0.138661 1.15074C0.0125983 1.49237 -0.0287212 1.87064 0.0196848 2.23992C0.0680907 2.6092 0.204155 2.95373 0.411471 3.23197L5.61406 9.26329C5.7894 9.49535 6.00305 9.68075 6.24132 9.80761C6.4796 9.93447 6.73724 10 6.99775 10C7.25826 10 7.5159 9.93447 7.75417 9.80761C7.99244 9.68075 8.20609 9.49535 8.38144 9.26329Z" fill="#797979"/>
              </svg>
            </span>
          </div>

          {/* Dropdown Menu */}
          {isOpen && (
            <div className="absolute z-50 w-full mt-1 max-h-60 overflow-y-auto"
              style={{
                borderRadius: '12px',
                border: '1px solid #E2D5FF',
                background: '#FFF',
                boxShadow: '0 4px 16px 0 rgba(0, 0, 0, 0.25)'
              }}
            >
              {options.map((option) => (
                <div
                  key={option}
                  className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    let newArray;
                    if (selectedValues.includes(option)) {
                      newArray = selectedValues.filter(item => item !== option);
                    } else {
                      newArray = [...selectedValues, option];
                    }
                    setFormData((prev: any) => ({
                      ...prev,
                      [name]: newArray
                    }));
                  }}
                >
                  <div className="relative mr-3">
                    <div 
                      className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
                        selectedValues.includes(option)
                          ? 'bg-[#564DB8] border-[#4A3B8E]'
                          : 'bg-white border-[#4A3B8E]'
                      }`}
                    >
                      {selectedValues.includes(option) && (
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </div>
                  <span className="text-sm text-gray-700">{option}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Other fields */}
        {showOtherField && selectedValues.includes('Others') && (
          <div className="mt-4">
            <input
              type="text"
              name={otherFieldName}
              value={otherFieldValue}
              onChange={handleInputChange}
              placeholder="Please specify..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:border-[#564DB8]"
            />
          </div>
        )}

        {showOtherField && name === 'notWorking' && selectedValues.includes('others') && (
          <div className="mt-4">
            <input
              type="text"
              name="notWorkingOther"
              value={otherFieldValue}
              onChange={handleInputChange}
              placeholder="Please specify what's not working..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:outline-none focus:border-[#564DB8]"
            />
          </div>
        )}
      </div>
    );
  });

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      Array.from(files).forEach(file => {
        const fileUpload: FileUpload = {
          id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
          name: file.name,
          size: file.size,
          status: 'uploading',
          progress: 0
        };
        
        setUploadedFiles(prev => [...prev, fileUpload]);
        
        // Simulate file upload progress
        simulateUpload(fileUpload.id);
      });
    }
  };

  const simulateUpload = (fileId: string) => {
    let progress = 0;
    const interval = setInterval(() => {
      progress += Math.random() * 30;
      if (progress >= 100) {
        progress = 100;
        setUploadedFiles(prev => 
          prev.map(file => 
            file.id === fileId 
              ? { ...file, status: 'uploaded', progress: 100 }
              : file
          )
        );
        clearInterval(interval);
      } else {
        setUploadedFiles(prev => 
          prev.map(file => 
            file.id === fileId 
              ? { ...file, progress }
              : file
          )
        );
      }
    }, 200);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.projectName || !formData.brandName || !formData.productInfo) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate assets section
    if (formData.hasAssets) {
      // If assets are available, at least one asset link should be provided
      const hasAssetLinks = formData.whiteBackgroundLink || 
                           formData.oldImagesLink || 
                           formData.lifestyleImageLink || 
                           formData.userGeneratedContentLink || 
                           formData.videoContentLink;
      
      if (!hasAssetLinks) {
        toast.error('Please provide at least one asset link when assets are available');
        return;
      }
    } else {
      // If assets are not available, reason is required
      if (!formData.noAssetsReason.trim()) {
        toast.error('Please provide a reason for not having assets available');
        return;
      }
    }
    
    try {
      // Show loading state immediately
      toast.loading('Submitting form...', { id: 'form-submit' });
      // Capture submission date for the loading header
      setSubmissionDate(new Date());
      
      // Prepare data for API
      const apiData = {
        project_name: formData.projectName,
        brand_name: formData.brandName,
        product_info: formData.productInfo,
        amazon_listing_link: formData.amazonListingLink,
        website_link: formData.websiteLink,
        instagram_link: formData.instagramLink,
        current_product_info: formData.currentProductInfo,
        listing_elements: formData.listingElements,
        product_category: formData.productCategory,
        category_other: formData.categoryOther,
        not_working: formData.notWorking,
        not_working_other: formData.notWorkingOther,
        has_assets: formData.hasAssets,
        white_background_link: formData.whiteBackgroundLink,
        old_images_link: formData.oldImagesLink,
        lifestyle_image_link: formData.lifestyleImageLink,
        user_generated_content_link: formData.userGeneratedContentLink,
        video_content_link: formData.videoContentLink,
        no_assets_reason: formData.noAssetsReason
      };
      
      const response = await generateOnboardingForm.mutateAsync(apiData);

      if (response.status === 'processing' && response.task_id) {
        toast.success('Form submitted! Generating onboarding form...', { id: 'form-submit' });
        setTaskId(response.task_id);
        if (response.project_id) {
          setSubmittedProjectId(response.project_id.toString());
          // Immediately navigate to creative brief page; it will poll until ready
          navigate(`/creative-brief/${response.project_id}`);
        }
        setIsGeneratingContent(true);
      } else if (response.success && (response as any).form) {
        setGeneratedForm((response as any).form);
        toast.success('Onboarding form generated', { id: 'form-submit' });
      } else {
        toast.success('Form submitted', { id: 'form-submit' });
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to submit form. Please try again.', { id: 'form-submit' });
    }
  };

  // Store submission date to display in loading header
  const [submissionDate, setSubmissionDate] = useState<Date | null>(null);

  // Show loading state when generating onboarding form
  if (isGeneratingContent) {
    return (
      <div className="min-h-screen bg-gradient-to-tr from-[#E9E3FF] to-[#F8F6FF]" style={{
        backgroundImage: bgReady && !lowPerfMode ? `url(${bgPattern})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        willChange: 'transform'
      }}>
        <div className="container mx-auto px-4 flex items-center justify-center" style={{paddingTop: 144, paddingBottom: 90}}>
          <div className="bg-white shadow-lg p-8 rounded-2xl text-center" style={{ width: 600 }}>
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#6B4EFF] mx-auto mb-6"></div>
            <h2 className="font-roboto font-semibold text-2xl text-[#4A3B8E] mb-2">
              Generating Onboarding Form: {formData.projectName || 'Untitled Project'}
            </h2>
            <div className="text-sm text-[#85878B] mb-4">
              {submissionDate ? submissionDate.toLocaleDateString() : new Date().toLocaleDateString()}
            </div>
            <p className="text-[#85878B] mb-4">Creating a filled onboarding form from your inputs...</p>
            <div className="text-sm text-[#6B4EFF]">Processing onboarding form...</div>
            <div className="text-xs text-[#85878B] mt-2">
              This may take 2-3 minutes. Please don't close this page.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gradient-to-tr from-[#E9E3FF] to-[#F8F6FF]" style={{
          backgroundImage: bgReady ? `url(${bgPattern})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}>
        <div className="container mx-auto px-4" style={{paddingTop: 144, paddingBottom: 90}}>
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="font-roboto font-semibold text-3xl text-[#4A3B8E]" style={{ fontSize: 48, marginBottom: 16 }}>Welcome To Listing Onboarding Form</h1>
            <p className="mb-8" style={{ color: '#4A3B8E', fontSize: 18, fontWeight: 400 }}>Kickstart Your Amazon Success</p>
          </div>

          {/* Render generated onboarding form if available */}
          {generatedForm && (
            <div className="bg-white shadow-md mx-auto p-8 mb-8" style={{ width: 1160, borderRadius: '20px' }}>
              <h2 className="font-roboto font-semibold mb-4" style={{ fontSize: 28, color: '#4A3B8E' }}>Generated Onboarding Form</h2>
              <pre className="text-sm overflow-auto" style={{ maxHeight: 400 }}>{JSON.stringify(generatedForm, null, 2)}</pre>
            </div>
          )}

          {/* Main Form */}
          <div className="bg-white shadow-md mx-auto p-8" style={{ width: 1160, borderRadius: '20px', transform: 'translateZ(0)' }}>
            <div className="mb-8">
              <h2 className="font-roboto font-semibold mb-4" style={{ fontSize: 32, color: '#4A3B8E' }}>Let's Get Things Rolling</h2>
              <p className="mb-8" style={{ color: '#85878B', fontSize: 18, fontWeight: 400 }}>
                Your project officially kicks off once your form is submitted, and We get access to your assets folder. 
                This won't take more than 10 minutes. Most of the questions are simple links or quick references. Once you drop those in, our Al pulls 
                the details and auto-fills the form for you, so you're not stuck wasting time typing things out. You'll get a clean, ready-to-review draft. 
                Just give each section a quick thumbs-up or tweak anything that needs fixing.
              </p>
            </div>

            <form className="space-y-8" onSubmit={handleSubmit}>
              {/* Project & Brand Section */}
              <div>
                <h3 className="font-roboto font-semibold text-lg text-[#4A3B8E] mb-4">Project & Brand</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Project Name</label>
                    <input
                      type="text"
                      name="projectName"
                      value={formData.projectName}
                      onChange={handleInputChange}
                      placeholder="Project Name"
                      className="w-full px-3 py-2 focus:outline-none focus:border-[#564DB8]"
                      style={{
                        borderRadius: 12,
                        border: '1px solid #E2D5FF',
                        background: 'rgba(244, 243, 255, 0.60)'
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Brand Name</label>
                    <input
                      type="text"
                      name="brandName"
                      value={formData.brandName}
                      onChange={handleInputChange}
                      placeholder="Brand Name"
                      className="w-full px-3 py-2 focus:outline-none focus:border-[#564DB8]"
                      style={{
                        borderRadius: 12,
                        border: '1px solid #E2D5FF',
                        background: 'rgba(244, 243, 255, 0.60)'
                      }}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">What product(s) is this form about? *</label>
                  <textarea
                    name="productInfo"
                    value={formData.productInfo}
                    onChange={handleInputChange}
                    placeholder="What product(s) is this form about?"
                    rows={3}
                    className="w-full px-3 py-2 focus:outline-none focus:border-[#564DB8]"
                    style={{
                      borderRadius: 12,
                      border: '1px solid #E2D5FF',
                      background: 'rgba(244, 243, 255, 0.60)'
                    }}
                  />
                </div>
              </div>

              {/* Links Section */}
              <div>
                <h3 className="font-roboto font-semibold text-lg text-[#4A3B8E] mb-4">Links</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Amazon Listing Link (if any)</label>
                    <input
                      type="url"
                      name="amazonListingLink"
                      value={formData.amazonListingLink}
                      onChange={handleInputChange}
                      placeholder="Amazon Listing Link (if any)"
                      className="w-full px-3 py-2 focus:outline-none focus:border-[#564DB8]"
                      style={{
                        borderRadius: 12,
                        border: '1px solid #E2D5FF',
                        background: 'rgba(244, 243, 255, 0.60)'
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Website Link</label>
                    <input
                      type="url"
                      name="websiteLink"
                      value={formData.websiteLink}
                      onChange={handleInputChange}
                      placeholder="Website Link"
                      className="w-full px-3 py-2 focus:outline-none focus:border-[#564DB8]"
                      style={{
                        borderRadius: 12,
                        border: '1px solid #E2D5FF',
                        background: 'rgba(244, 243, 255, 0.60)'
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Instagram Link</label>
                    <input
                      type="url"
                      name="instagramLink"
                      value={formData.instagramLink}
                      onChange={handleInputChange}
                      placeholder="Instagram Link"
                      className="w-full px-3 py-2 focus:outline-none focus:border-[#564DB8]"
                      style={{
                        borderRadius: 12,
                        border: '1px solid #E2D5FF',
                        background: 'rgba(244, 243, 255, 0.60)'
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Link to the most current product information (if different)</label>
                    <textarea
                      name="currentProductInfo"
                      value={formData.currentProductInfo}
                      onChange={handleInputChange}
                      placeholder="Link to the most current product information (if different)"
                      rows={3}
                      className="w-full px-3 py-2 focus:outline-none focus:border-[#564DB8]"
                      style={{
                        borderRadius: 12,
                        border: '1px solid #E2D5FF',
                        background: 'rgba(244, 243, 255, 0.60)'
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Listing Elements & Category */}
              <div>
                <h3 className="font-roboto font-semibold text-lg text-[#4A3B8E] mb-4">Listing Elements & Category</h3>
                <div className="space-y-4">
                  <MultiSelectDropdown
                    label="What elements of the listing are we working on?"
                    name="listingElements"
                    options={listingElementsOptions}
                    selectedValues={formData.listingElements}
                    isOpen={dropdownOpen['listingElements']}
                    setIsOpen={(open) => setDropdownOpen(prev => ({ ...prev, listingElements: open }))}
                  />
                  <MultiSelectDropdown
                    label="Which category does your product belong to?"
                    name="productCategory"
                    options={categoryOptions}
                    selectedValues={formData.productCategory}
                    showOtherField={true}
                    otherFieldName="categoryOther"
                    otherFieldValue={formData.categoryOther}
                    isOpen={dropdownOpen['productCategory']}
                    setIsOpen={(open) => setDropdownOpen(prev => ({ ...prev, productCategory: open }))}
                  />
                </div>
              </div>

              {/* What's Not Working */}
              <div>
                <h3 className="font-roboto font-semibold text-lg text-[#4A3B8E] mb-4">What's Not Working In Your Current Amazon Listing?</h3>
                <MultiSelectDropdown
                  label="Select all that apply"
                  name="notWorking"
                  options={notWorkingOptions}
                  selectedValues={formData.notWorking}
                  showOtherField={true}
                  otherFieldName="notWorkingOther"
                  otherFieldValue={formData.notWorkingOther}
                  isOpen={dropdownOpen['notWorking']}
                  setIsOpen={(open) => setDropdownOpen(prev => ({ ...prev, notWorking: open }))}
                />
              </div>

              {/* Assets Section */}
              <div>
                <h3 className="font-roboto font-semibold text-lg text-[#4A3B8E] mb-4">Assets *</h3>
                <div className="mb-4 flex items-center gap-3">
                  <button
                    type="button"
                    aria-pressed={formData.hasAssets}
                    onClick={() => setFormData(prev => ({ ...prev, hasAssets: !prev.hasAssets }))}
                    className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#564DB8] focus:ring-offset-2 ${
                      formData.hasAssets 
                        ? 'bg-[#564DB8]' 
                        : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition-transform duration-300 ease-in-out ${
                        formData.hasAssets 
                          ? 'translate-x-7' 
                          : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <span className="text-lg text-gray-700 font-roboto font-medium">Do you have assets available?</span>
                </div>
                
                {formData.hasAssets ? (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">White Background Image Link</label>
                      <input
                        type="url"
                        name="whiteBackgroundLink"
                        value={formData.whiteBackgroundLink}
                        onChange={handleInputChange}
                        placeholder="White Background Image Link"
                        className="w-full px-3 py-2 focus:outline-none focus:border-[#564DB8]"
                        style={{
                          borderRadius: 12,
                          border: '1px solid #E2D5FF',
                          background: 'rgba(244, 243, 255, 0.60)'
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Old Images Link</label>
                      <input
                        type="url"
                        name="oldImagesLink"
                        value={formData.oldImagesLink}
                        onChange={handleInputChange}
                        placeholder="Old Images Link"
                        className="w-full px-3 py-2 focus:outline-none focus:border-[#564DB8]"
                        style={{
                          borderRadius: 12,
                          border: '1px solid #E2D5FF',
                          background: 'rgba(244, 243, 255, 0.60)'
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Lifestyle Image Link</label>
                      <input
                        type="url"
                        name="lifestyleImageLink"
                        value={formData.lifestyleImageLink}
                        onChange={handleInputChange}
                        placeholder="Lifestyle Image Link"
                        className="w-full px-3 py-2 focus:outline-none focus:border-[#564DB8]"
                        style={{
                          borderRadius: 12,
                          border: '1px solid #E2D5FF',
                          background: 'rgba(244, 243, 255, 0.60)'
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">User Generated Content Link</label>
                      <input
                        type="url"
                        name="userGeneratedContentLink"
                        value={formData.userGeneratedContentLink}
                        onChange={handleInputChange}
                        placeholder="User Generated Content Link"
                        className="w-full px-3 py-2 focus:outline-none focus:border-[#564DB8]"
                        style={{
                          borderRadius: 12,
                          border: '1px solid #E2D5FF',
                          background: 'rgba(244, 243, 255, 0.60)'
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Video Content Link</label>
                      <input
                        type="url"
                        name="videoContentLink"
                        value={formData.videoContentLink}
                        onChange={handleInputChange}
                        placeholder="Video Content Link"
                        className="w-full px-3 py-2 focus:outline-none focus:border-[#564DB8]"
                        style={{
                          borderRadius: 12,
                          border: '1px solid #E2D5FF',
                          background: 'rgba(244, 243, 255, 0.60)'
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Reason for not having assets *</label>
                    <textarea
                      name="noAssetsReason"
                      value={formData.noAssetsReason}
                      onChange={handleInputChange}
                      placeholder="Please explain why you don't have assets available (e.g., new product, budget constraints, etc.)"
                      rows={4}
                      className="w-full px-3 py-2 focus:outline-none focus:border-[#564DB8] resize-none"
                      style={{
                        borderRadius: 12,
                        border: '1px solid #E2D5FF',
                        background: 'rgba(244, 243, 255, 0.60)'
                      }}
                    />
                  </div>
                )}

                {formData.hasAssets && (
                  <>
                    {/* Hidden File Input */}
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      multiple
                      accept="*/*"
                      className="hidden"
                    />

                    {/* File Upload Area */}
                    <div className="mt-6 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                      <div className="flex flex-col items-center">
                        <div className="w-12 h-12 bg-[#564DB8] rounded-lg flex items-center justify-center mb-4">
                          <svg xmlns="http://www.w3.org/2000/svg" width="42" height="42" viewBox="0 0 42 42" fill="none">
                            <g clipPath="url(#clip0_1_5444)">
                              <path d="M33.4422 3.12109H14.1748V11.1111H37.5572V7.23451C37.5572 4.96616 35.7112 3.12109 33.4422 3.12109Z" fill="#564DB8" fillOpacity="0.4"/>
                              <path d="M22.5352 12.3403H0V4.92636C0 2.20972 2.21068 0 4.92828 0H12.1336C12.8497 0 13.5396 0.150925 14.1664 0.434509C15.0418 0.828964 15.7939 1.47913 16.3213 2.3286L22.5352 12.3403Z" fill="#272264"/>
                              <path d="M42 14.0004V37.8817C42 40.153 40.1511 42.0003 37.8789 42.0003H4.12111C1.84891 42.0003 0 40.153 0 37.8817V9.88086H37.8789C40.1511 9.88086 42 11.7288 42 14.0004Z" fill="#5F4DBE"/>
                              <path d="M42 14.0004V37.8817C42 40.153 40.1511 42.0003 37.8789 42.0003H21V9.88086H37.8789C40.1511 9.88086 42 11.7288 42 14.0004Z" fill="#9181F4"/>
                              <path d="M32.048 25.9395C32.048 32.032 27.0919 36.9884 21.0001 36.9884C14.9083 36.9884 9.95215 32.032 9.95215 25.9395C9.95215 19.8481 14.9083 14.8916 21.0001 14.8916C27.0919 14.8916 32.048 19.8481 32.048 25.9395Z" fill="#E7DDFF"/>
                              <path d="M24.5612 26.0758C24.3308 26.2709 24.0485 26.3661 23.7688 26.3661C23.4185 26.3661 23.0705 26.2177 22.827 25.9287L22.2307 25.2218V29.8499C22.2307 30.5292 21.6795 31.0803 21.0002 31.0803C20.3209 31.0803 19.7698 30.5292 19.7698 29.8499V25.2218L19.1734 25.9287C18.7344 26.4481 17.9587 26.5145 17.4392 26.0758C16.9201 25.6378 16.8535 24.8617 17.2915 24.3422L19.7271 21.4548C20.0447 21.0793 20.508 20.8633 21.0002 20.8633C21.4924 20.8633 21.9558 21.0793 22.2733 21.4548L24.7089 24.3422C25.147 24.8617 25.0803 25.6378 24.5612 26.0758Z" fill="#272264"/>
                            </g>
                            <defs>
                              <clipPath id="clip0_1_5444">
                                <rect width="42" height="42" fill="white"/>
                              </clipPath>
                            </defs>
                          </svg>
                        </div>
                        <p className="text-gray-600 mb-2 w-full text-center">Drag your file(s) to start uploading</p>
                        <div className="flex items-center justify-center w-full max-w-[250px] mb-6">
                          <div className="flex-1 h-px bg-gray-200" />
                          <span className="mx-4 text-gray-400 font-medium">OR</span>
                          <div className="flex-1 h-px bg-gray-200" />
                        </div>
                        <button 
                          type="button"
                          onClick={handleBrowseClick}
                          className="px-6 py-2 text-white font-medium rounded-lg"
                          style={{
                            background: 'linear-gradient(90deg, #362E7F 0%, #5A4ABE 45.67%, #7F60BF 100%)'
                          }}
                        >
                          Browse file
                        </button>
                      </div>
                    </div>
                  </>
                )}

                {/* File List */}
                {uploadedFiles.length > 0 && (
                  <div className="flex flex-col items-start w-full mt-8">
                    <div className="w-1/2">
                      {uploadedFiles.map((file) => (
                        file.status === 'uploading' ? (
                          <div key={file.id} className="border-[1.5px] border-[#E2D5FF] rounded-2xl shadow-sm p-6 bg-white flex flex-col gap-2 mb-4 w-full" style={{ minWidth: 320 }}>
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <div className="font-semibold text-lg mb-1">Uploading...</div>
                                <div className="flex items-center text-gray-500 text-base gap-2">
                                  <span>{file.progress ? Math.round(file.progress) : 0}%</span>
                                  <span className="mx-1">•</span>
                                  <span>30 seconds remaining</span>
                                </div>
                              </div>
                              <div className="flex gap-3 mt-1">
                                <button className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 bg-white">
                                  <svg width="16" height="16" fill="none" stroke="#797979" strokeWidth="2" viewBox="0 0 24 24"><rect x="6" y="4" width="4" height="16" rx="2"/><rect x="14" y="4" width="4" height="16" rx="2"/></svg>
                                </button>
                                <button className="w-8 h-8 flex items-center justify-center rounded-full border border-red-200 bg-white">
                                  <svg width="16" height="16" fill="none" stroke="#FF3B3B" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="#FF3B3B" strokeWidth="2"/><path d="M15 9L9 15M9 9l6 6" stroke="#FF3B3B" strokeWidth="2" strokeLinecap="round"/></svg>
                                </button>
                              </div>
                            </div>
                            <div className="w-full h-2 bg-gray-100 rounded-full">
                              <div className="h-2 bg-[#564DB8] rounded-full transition-all duration-300" style={{ width: `${file.progress || 0}%` }} />
                            </div>
                          </div>
                        ) : (
                          <div key={file.id} className="border-[1.5px] border-[#E2D5FF] rounded-2xl shadow-sm p-4 bg-white flex items-center justify-between mb-4 w-full" style={{ minWidth: 320 }}>
                            <div className="flex items-center gap-3 w-full border border-dashed border-[#A88AFF] rounded-xl p-2">
                              <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect width="40" height="40" rx="8" fill="#F6F3FF"/>
                                <rect x="8" y="8" width="24" height="24" rx="4" fill="#FFE082"/>
                                <rect x="8" y="28" width="24" height="4" rx="2" fill="#A88AFF"/>
                                <rect x="12" y="12" width="16" height="8" rx="2" fill="#FFF"/>
                                <rect x="12" y="22" width="8" height="2" rx="1" fill="#A88AFF"/>
                                <rect x="20" y="22" width="8" height="2" rx="1" fill="#A88AFF"/>
                                <rect x="12" y="32" width="8" height="2" rx="1" fill="#A88AFF"/>
                                <rect x="20" y="32" width="8" height="2" rx="1" fill="#A88AFF"/>
                                <rect x="8" y="32" width="4" height="2" rx="1" fill="#A88AFF"/>
                                <rect x="28" y="32" width="4" height="2" rx="1" fill="#A88AFF"/>
                                <rect x="8" y="36" width="24" height="2" rx="1" fill="#A88AFF"/>
                                <rect x="28" y="8" width="4" height="4" rx="2" fill="#A88AFF"/>
                                <rect x="8" y="8" width="4" height="4" rx="2" fill="#A88AFF"/>
                              </svg>
                              <div className="flex flex-col flex-1">
                                <span className="font-semibold text-base">{file.name}</span>
                                <span className="text-gray-500 text-sm">{formatFileSize(file.size)}</span>
                              </div>
                              <button className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 bg-white">
                                <svg width="16" height="16" fill="none" stroke="#797979" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="#797979" strokeWidth="2"/><path d="M15 9L9 15M9 9l6 6" stroke="#797979" strokeWidth="2" strokeLinecap="round"/></svg>
                              </button>
                            </div>
                          </div>
                        )
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <div className="pt-8">
                <button
                  type="submit"
                  disabled={generateOnboardingForm.isPending}
                  className="w-full py-3 text-white font-semibold text-lg rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background: 'linear-gradient(90deg, #362E7F 0%, #5A4ABE 45.67%, #7F60BF 100%)'
                  }}
                >
                  {generateOnboardingForm.isPending ? 'Submitting...' : 'Generate Brand Summary'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default ListingOnboardingForm; 